<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useLink } from '../../../composables/use-link';

type Props = {
  /** Title text, always occupying two lines so cards in a grid align. */
  title: string;
  /**
   * Router target. When set, the title renders as the host app's link
   * component (NuxtLink or RouterLink). Takes precedence over `href`.
   */
  to?: RouteLocationRaw;
  /** External/plain URL. When set (and `to` is not), renders as an `<a>`. */
  href?: string;
};

const props = defineProps<Props>();

const linkComponent = useLink();

// Binding `href` alongside `to` — even as `undefined` — overwrites the href a
// router link generates, leaving a dead anchor. Only ever bind one of them.
const linkProps = computed(() => {
  if (props.to !== undefined) {
    return { to: props.to };
  }

  if (props.href !== undefined) {
    return { href: props.href };
  }

  return {};
});

const tag = computed(() => {
  if (props.to !== undefined) {
    return linkComponent;
  }

  if (props.href !== undefined) {
    return 'a';
  }

  return 'h3';
});
</script>

<template>
  <component
    :is="tag"
    v-bind="linkProps"
    class="text-body-lg line-clamp-2 min-h-[2lh] w-full font-bold text-on-surface-primary"
  >
    <slot>{{ props.title }}</slot>
  </component>
</template>
